import React, { useCallback, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";
import { StoreState } from "enum/StoreState";
import { CreateDashboardModalProps } from "../types";

/**
 * CreateDashboardModal - Inline modal for creating a new dashboard
 * Simple form with name and key (initials) fields
 *
 * FR-014: Modal inline for quick dashboard creation
 */
export const CreateDashboardModal: React.FC<CreateDashboardModalProps> =
  observer(({ isOpen, onClose, onDashboardCreated, t }) => {
    const {
      dataStores: { operationRuleStore },
    } = useStores();

    const toast = useToast();

    const [name, setName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ name?: string }>({});

    // Validate form
    const validate = useCallback(() => {
      const newErrors: { name?: string } = {};

      if (!name.trim()) {
        newErrors.name = "requiredField";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [name]);

    // Handle form submission
    const handleSubmit = useCallback(async () => {
      if (!validate()) {
        return;
      }

      setIsSubmitting(true);
      try {
        await operationRuleStore.addOperationRule(name.trim());

        if (operationRuleStore.state === StoreState.DONE) {
          // Get the newly created dashboard ID (first in the list after creation)
          const newDashboard = operationRuleStore.operationRules[0];

          toast({
            title: t("dashboardCreatedSuccess"),
            status: "success",
            position: "bottom-right",
            isClosable: true,
          });

          // Reset form
          setName("");
          setErrors({});

          // Notify parent with new dashboard ID
          if (newDashboard) {
            onDashboardCreated(newDashboard.id);
          } else {
            onClose();
          }
        } else {
          toast({
            title: t("errorCreatingDashboard"),
            status: "error",
            position: "bottom-right",
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          title: t("errorCreatingDashboard"),
          status: "error",
          position: "bottom-right",
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [
      validate,
      name,
      operationRuleStore,
      toast,
      t,
      onDashboardCreated,
      onClose,
    ]);

    // Handle modal close
    const handleClose = useCallback(() => {
      setName("");
      setErrors({});
      onClose();
    }, [onClose]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="md"
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent bg="white" borderRadius="md">
          <ModalHeader color="#313f4d" fontSize="20px" fontWeight="600">
            {t("createDashboard")}
          </ModalHeader>
          <ModalCloseButton
            top={4}
            right={4}
            color="gray.500"
            _hover={{ color: "gray.700" }}
          />

          <ModalBody pb={6}>
            <Stack spacing={5}>
              {/* Dashboard Name */}
              <FormControl isInvalid={!!errors.name} isRequired>
                <FormLabel color="#414651" fontSize="14px" fontWeight="500">
                  {t("dashboardName")}
                </FormLabel>
                <Input
                  placeholder={t("dashboardNamePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  bg="white"
                  color="#1a1a1a"
                  borderColor="#D5D7DA"
                  _placeholder={{ color: "#717680" }}
                  _hover={{ borderColor: "#A1A1AA" }}
                  _focus={{
                    borderColor: "#0884CC",
                    boxShadow: "0 0 0 1px #0884CC",
                    color: "#1a1a1a",
                  }}
                  h="44px"
                  fontSize="16px"
                />
                {errors.name && (
                  <FormErrorMessage>{t(errors.name)}</FormErrorMessage>
                )}
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter gap={4}>
            <Button
              variant="outline"
              bg="white"
              borderColor="#086aa9"
              borderWidth="1px"
              color="#086aa9"
              borderRadius="8px"
              fontSize="16px"
              fontWeight="400"
              h="40px"
              px={6}
              _hover={{ bg: "rgba(8, 106, 169, 0.08)" }}
              onClick={handleClose}
            >
              {t("cancel")}
            </Button>
            <Button
              bg="#086aa9"
              color="white"
              borderRadius="8px"
              fontSize="16px"
              fontWeight="400"
              h="40px"
              px={6}
              _hover={{ bg: "#075a8c" }}
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              {t("create")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  });

CreateDashboardModal.displayName = "CreateDashboardModal";

export default CreateDashboardModal;
